
import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface TemplateProps {
  profile: UserProfile;
  client?: Client | null;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: string;
  containerClass: string;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  hideHeader?: boolean;
}

const RetroTemplate: React.FC<TemplateProps> = ({
  profile, client, title, metaData, children, footerNote, type, containerClass, LogoImage, hideHeader = false
}) => {
  return (
    <div className={`${containerClass} bg-[#fefcf5] font-mono text-neutral-800`}>
       {/* Paper Texture Noise (Simulated with CSS) */}
       <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>

       <div className="p-12 h-full flex flex-col relative z-10">
           {!hideHeader && (
             <>
               {/* Header Stamp */}
               <div className="text-center mb-8 border-b-2 border-dashed border-neutral-400 pb-8">
                   <div className="inline-block border-4 border-neutral-800 p-4 mb-4 transform -rotate-2">
                       <h1 className="text-4xl font-bold uppercase tracking-tighter">{profile.name}</h1>
                       <p className="text-xs mt-1">EST. {new Date(profile.joinedAt).getFullYear()}</p>
                   </div>
                   <div className="text-sm space-y-1">
                       <p>{profile.address}</p>
                       <p>{profile.phone} | {profile.email}</p>
                   </div>
               </div>

               <div className="flex justify-between items-start mb-8">
                   <div className="bg-white border border-neutral-300 p-4 shadow-sm transform -rotate-1 max-w-[45%]">
                       <p className="text-xs font-bold underline mb-2">SOLD TO:</p>
                       {client ? (
                           <div>
                               <p className="text-lg font-bold leading-tight">{client.name}</p>
                               <p className="text-sm">{client.company}</p>
                               <p className="text-xs mt-1">{client.address}</p>
                           </div>
                       ) : <p>CASH CLIENT</p>}
                   </div>

                   <div className="text-right">
                       <h2 className="text-3xl font-bold uppercase mb-2">{type}</h2>
                       <div className="inline-block text-left border border-neutral-800 p-2">
                           {metaData.map((m, i) => (
                               <div key={i} className="flex justify-between gap-4 text-sm">
                                   <span className="uppercase">{m.label}</span>
                                   <span className="font-bold">{m.value}</span>
                               </div>
                           ))}
                       </div>
                   </div>
               </div>

               <div className="border-y-2 border-neutral-800 py-1 mb-8">
                   <div className="border-y border-dashed border-neutral-400 py-1 text-center text-xs uppercase tracking-[0.2em]">
                       {title}
                   </div>
               </div>
             </>
           )}

           <div className="flex-1 retro-table">
               {children}
           </div>

           <div className="mt-auto pt-8 text-center">
               <p className="mb-4">***************************************</p>
               <p className="text-sm font-bold uppercase max-w-md mx-auto">{footerNote}</p>
               <p className="mt-4">***************************************</p>
               <p className="text-xs mt-2">THANK YOU FOR YOUR BUSINESS</p>
           </div>
       </div>
    </div>
  );
};

export default RetroTemplate;
